// Static presentation content mirroring the NVSN data room HTML.
// Files & links per section are dynamic (from the backend); this holds the rich copy.

export const LOGO_URL = "/nvsn-logo.png";

export const OVERVIEW = {
  eyebrow: "Data Room · Pre-Seed 2026",
  stats: [
    { value: "$3.5M", label: "Raise" },
    { value: "SAFE", label: "Instrument" },
    { value: "$25M", label: "Valuation Cap" },
  ],
  summary: [
    {
      label: "The Problem",
      body: "People don’t fail at their health — the infrastructure fails them. Today’s wellness consumer stacks five or more products at $700+ a month, from wearables and gyms to supplements, meal plans, and apps, and each one operates as its own location and data silo. That leaves the consumer to do the integrating: calculating macros by hand, taking supplements blind, and booking recovery by feel. Wearables were supposed to solve this, but each one covers only a slice of the body — WHOOP for performance and recovery, Oura for sleep, Garmin for training, etc — so consumers either choose which metrics matter or stack devices to fill the gaps. Even then, the data has holes, because bands come off when they don’t fit the occasion. The deeper problem is that no wearable sees what goes into the body: what you ate, how you trained, how you recovered. As a result, they can only report what happened. When a recovery score drops, they can’t tell you whether it was the late meal, the leg day, or the short night. Answering why takes training, nutrition, and sleep data captured together, and no one captures them together.",
    },
    {
      label: "The Solution",
      body: "NVSN is the world’s first closed-loop health and performance ecosystem. It spans six pillars — Lifestyle, Athleisure, Nutrition, Technology, Recovery, and Clubhouse — connected by one intelligence layer. Competitors measure outputs. NVSN captures the inputs: what members eat, how they train, and how they recover. That data flows into one lifetime health profile, so the ecosystem can connect cause to effect.",
    },
    {
      label: "The Sequence",
      body: "NVSN builds the audience before the hardware. SATURDAYS, a sponsor-funded weekly run club, launches in Charlotte in April 2028 and expands to 26 cities. Athleisure ships alongside it. The NVSN wearable launches in 2030 to an owned audience instead of a paid one. The Charlotte Clubhouse opens in 2031 and closes the loop. Each pillar lowers acquisition cost for the next.",
    },
    {
      label: "The Moat",
      body: "No single-vertical competitor captures inputs across training, nutrition, and recovery under one identity. The correlated dataset compounds with every interaction, and the ecosystem that generates it is an architecture, not a feature to bolt on. The unified system is patent pending.",
    },
    {
      label: "Where We Are",
      body: "Provisional patent filed. Represented by Cooley LLP. Charlotte flagship site under contract. Advisory leadership from WHOOP, Peloton, Fitbit, Tonal, and the NFL. The Founder is a former cadet-athlete at West Point who lived inside a closed-loop performance system — and its absence afterward.",
    },
    {
      label: "The Raise",
      body: "$3.5M SAFE at a $25M post-money cap, funding 18 months to deliver:\n• a working wearable prototype\n• an app and AI MVP\n• a closed beta with real usage data\n• non-provisional and PCT patent filings\n• technical leadership for hardware and software\n• SATURDAYS and Athleisure launch\n\nThat positions NVSN for a seed round on working technology, protected IP, and live revenue from SATURDAYS and Athleisure.",
    },
    {
      label: "Why Now",
      body: "Incumbents have proven people will pay for premium health in pieces. WHOOP is valued at $10.1B and is expanding toward clinical health. Oura is looking toward a $16B IPO, but delayed due to \"market uncertainty.\" Well, this is where the market is going and we're pioneering it. AI has made cross-domain synthesis feasible for the first time. The integrated system can’t be assembled through acquisition; it has to be designed as one from day one. NVSN already has been and it is protected.",
    },
  ],
  pillars: [
    { num: "01", name: "Lifestyle", sub: "SATURDAYS · run club · community · GTM engine", launch: "Spring 2028" },
    { num: "02", name: "Athleisure", sub: "DTC apparel · in-Clubhouse retail · brand presence", launch: "Spring 2028" },
    { num: "03", name: "Nutrition", sub: "Smart nutrition · supplements · smart POS", launch: "Spring 2029" },
    { num: "04", name: "Technology", sub: "Wearable · digital twin · AI engine · B2B", launch: "Spring 2030" },
    { num: "05", name: "Recovery", sub: "In-Clubhouse modalities · boutiques · biometric capture", launch: "Spring 2031" },
    { num: "06", name: "Clubhouse", sub: "Flagship physical destination · memberships · F&B · retail", launch: "Spring 2031" },
  ],
};

// eyebrow / badge + meta cards per slug
export const SECTION_META = {
  overview: { eyebrow: "Data Room · Pre-Seed 2026" },
  pitchdeck: { eyebrow: "Overview" },
  lifestyle: {
    badge: "Pillar 01 · Lifestyle",
    meta: [
      { label: "Launch", value: "Spring 2028 · 26 cities" },
      { label: "Operating Entity", value: "NVSN Lifestyle Holding LLC" },
      { label: "Revenue Type", value: "Sponsorship, events, data capture" },
      { label: "Strategic Role", value: "GTM engine · community flywheel · member acquisition" },
    ],
  },
  athleisure: {
    badge: "Pillar 02 · Athleisure",
    meta: [
      { label: "Launch", value: "Spring 2028" },
      { label: "Operating Entity", value: "NVSN Athleisure Holding LLC" },
      { label: "Revenue Type", value: "DTC e-commerce, in-Clubhouse retail" },
      { label: "Strategic Role", value: "Brand visibility · physical touchpoint · revenue bridge" },
    ],
  },
  nutrition: {
    badge: "Pillar 03 · Nutrition",
    meta: [
      { label: "Launch", value: "Spring 2029" },
      { label: "Operating Entity", value: "NVSN Nutrition Holding LLC" },
      { label: "Revenue Type", value: "F&B, supplement subscriptions, licensing" },
      { label: "Strategic Role", value: "Daily habit formation · highest frequency touchpoint" },
    ],
  },
  technology: {
    badge: "Pillar 04 · Technology",
    meta: [
      { label: "Launch", value: "Spring 2030" },
      { label: "Operating Entity", value: "NVSN Technology Holding LLC" },
      { label: "Revenue Type", value: "Hardware, subscription, B2B, licensing" },
      { label: "Strategic Role", value: "Intelligence layer · data moat · highest margin pillar" },
    ],
  },
  recovery: {
    badge: "Pillar 05 · Recovery",
    meta: [
      { label: "Launch", value: "Spring 2031 · Clubhouse opening" },
      { label: "Operating Entity", value: "NVSN Recovery Holding LLC" },
      { label: "Revenue Type", value: "Membership tier, service upsells, boutiques" },
      { label: "Strategic Role", value: "Retention driver · premium differentiation · data density" },
    ],
  },
  clubhouse: {
    badge: "Pillar 06 · Clubhouse",
    meta: [
      { label: "Flagship Opening", value: "Spring 2031 · Charlotte, NC" },
      { label: "Operating Entity", value: "NVSN Clubhouse Holding LLC" },
      { label: "Revenue Type", value: "Memberships, services, retail, F&B, events" },
      { label: "Capital Structure", value: "Developer-financed · 20-yr leaseback · zero construction risk" },
    ],
  },
  architecture: { eyebrow: "Diligence" },
  financials: { eyebrow: "Diligence" },
  legal: { eyebrow: "Diligence" },
  realestate: { eyebrow: "Diligence" },
  team: { eyebrow: "Diligence" },
};
